import { useState, useId } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FileText,
  Plus,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Globe,
  Menu,
  GripVertical,
  Loader2,
  AlertCircle,
  X,
  Check,
  ExternalLink,
  Image as ImageIcon,
  Type,
  AlignLeft,
  Columns,
  Minus,
  ArrowUpDown,
  Video,
  MousePointerClick,
  LayoutTemplate,
  Sparkles,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface Page {
  id: string;
  title: string;
  slug: string;
  content: { blocks: Block[] };
  excerpt?: string;
  status: 'draft' | 'published';
  publishedAt: string | null;
  metaTitle?: string;
  metaDescription?: string;
  template: string;
  showInNav: boolean;
  navOrder: number;
  parentId?: string;
  parent?: { id: string; title: string; slug: string };
  createdAt: string;
  updatedAt: string;
}

interface Block {
  id: string;
  type: string;
  data: Record<string, unknown>;
}

interface MediaItem {
  id: string;
  filename: string;
  originalName: string;
  mimeType: string;
  size: number;
  url: string;
  altText?: string;
}

type TabId = 'pages' | 'navigation';

const tabs: { id: TabId; name: string; icon: typeof FileText }[] = [
  { id: 'pages', name: 'Pages', icon: FileText },
  { id: 'navigation', name: 'Navigation', icon: Menu },
];

// ============================================
// Block palette definition
// ============================================

interface BlockPaletteItem {
  type: string;
  label: string;
  icon: typeof FileText;
  description: string;
}

const blockPalette: BlockPaletteItem[] = [
  { type: 'hero', label: 'Hero', icon: Sparkles, description: 'Full-width hero banner' },
  { type: 'heading', label: 'Heading', icon: Type, description: 'Section heading' },
  { type: 'paragraph', label: 'Text', icon: AlignLeft, description: 'Rich text paragraph' },
  { type: 'image', label: 'Image', icon: ImageIcon, description: 'Image with caption' },
  { type: 'cta', label: 'Call to Action', icon: MousePointerClick, description: 'Button link' },
  { type: 'video', label: 'Video', icon: Video, description: 'YouTube/Vimeo embed' },
  { type: 'divider', label: 'Divider', icon: Minus, description: 'Horizontal rule' },
  { type: 'spacer', label: 'Spacer', icon: ArrowUpDown, description: 'Adjustable space' },
  { type: 'columns', label: 'Columns', icon: Columns, description: '2 or 3 column layout' },
];

// ============================================
// Utility: generate block ID
// ============================================

let blockIdCounter = 0;
function generateBlockId(): string {
  blockIdCounter += 1;
  return `block-${Date.now()}-${blockIdCounter}`;
}

function ensureBlockIds(blocks: Block[]): Block[] {
  return blocks.map((b) => ({
    ...b,
    id: b.id || generateBlockId(),
  }));
}

// ============================================
// Default block data
// ============================================

function getDefaultBlockData(type: string): Record<string, unknown> {
  switch (type) {
    case 'hero':
      return { title: '', subtitle: '' };
    case 'paragraph':
      return { text: '' };
    case 'heading':
      return { level: 'h2', text: '' };
    case 'image':
      return { url: '', alt: '', caption: '' };
    case 'cta':
      return { text: '', url: '', style: 'primary' };
    case 'video':
      return { url: '', caption: '' };
    case 'divider':
      return { style: 'solid' };
    case 'spacer':
      return { height: 48 };
    case 'columns':
      return {
        count: 2,
        columns: [
          { blocks: [] },
          { blocks: [] },
        ],
      };
    default:
      return {};
  }
}

// ============================================
// Main component
// ============================================

export function WebsitePage() {
  const [activeTab, setActiveTab] = useState<TabId>('pages');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Website</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your public website pages and navigation
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'flex items-center gap-2 border-b-2 py-3 px-1 text-sm font-medium transition-colors',
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'pages' && <PagesTab />}
      {activeTab === 'navigation' && <NavigationTab />}
    </div>
  );
}

// ============================================
// PagesTab
// ============================================

function PagesTab() {
  const queryClient = useQueryClient();
  const [showEditor, setShowEditor] = useState(false);
  const [editingPage, setEditingPage] = useState<Page | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');

  const { data: pages, isLoading } = useQuery({
    queryKey: ['pages', statusFilter],
    queryFn: async () => {
      const params = statusFilter ? `?status=${statusFilter}` : '';
      const response = await api.get<Page[]>(`/pages${params}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/pages/${id}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  const publishMutation = useMutation({
    mutationFn: async ({ id, publish }: { id: string; publish: boolean }) => {
      const response = await api.post(`/pages/${id}/${publish ? 'publish' : 'unpublish'}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
    },
  });

  if (showEditor) {
    return (
      <PageEditor
        page={editingPage}
        onClose={() => {
          setShowEditor(false);
          setEditingPage(null);
        }}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          >
            <option value="">All Pages</option>
            <option value="published">Published</option>
            <option value="draft">Drafts</option>
          </select>
        </div>
        <button
          onClick={() => setShowEditor(true)}
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          New Page
        </button>
      </div>

      {pages?.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No pages yet</h3>
          <p className="text-gray-500 mt-1">Create your first page to get started.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Page</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">In Nav</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {pages?.map((page) => (
                <tr key={page.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{page.title}</p>
                      <p className="text-sm text-gray-500">/{page.slug}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={cn(
                        'inline-flex rounded-full px-2 py-1 text-xs font-medium',
                        page.status === 'published'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-700'
                      )}
                    >
                      {page.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {page.showInNav ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <span className="text-gray-300">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(page.updatedAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {page.status === 'published' && (
                        <a
                          href={`https://staging.openshul.org/${page.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1 text-gray-400 hover:text-gray-600"
                          title="View Page"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                      <button
                        onClick={() => {
                          setEditingPage(page);
                          setShowEditor(true);
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => publishMutation.mutate({ id: page.id, publish: page.status !== 'published' })}
                        disabled={publishMutation.isPending}
                        className={cn(
                          'p-1',
                          page.status === 'published'
                            ? 'text-green-500 hover:text-green-700'
                            : 'text-gray-400 hover:text-green-600'
                        )}
                        title={page.status === 'published' ? 'Unpublish' : 'Publish'}
                      >
                        {page.status === 'published' ? (
                          <Eye className="h-4 w-4" />
                        ) : (
                          <EyeOff className="h-4 w-4" />
                        )}
                      </button>
                      <button
                        onClick={() => {
                          if (confirm('Delete this page?')) {
                            deleteMutation.mutate(page.id);
                          }
                        }}
                        disabled={deleteMutation.isPending}
                        className="p-1 text-red-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============================================
// PageEditor with drag-and-drop
// ============================================

function PageEditor({ page, onClose }: { page: Page | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(page?.title || '');
  const [slug, setSlug] = useState(page?.slug || '');
  const [content, setContent] = useState<Block[]>(
    ensureBlockIds(page?.content?.blocks || [])
  );
  const [excerpt, setExcerpt] = useState(page?.excerpt || '');
  const [metaTitle, setMetaTitle] = useState(page?.metaTitle || '');
  const [metaDescription, setMetaDescription] = useState(page?.metaDescription || '');
  const [showInNav, setShowInNav] = useState(page?.showInNav || false);
  const [activeSection, setActiveSection] = useState<'content' | 'seo' | 'preview'>('content');
  const dndId = useId();

  // dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Auto-generate slug from title
  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!page) {
      setSlug(value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''));
    }
  };

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<Page>) => {
      if (page) {
        const response = await api.patch(`/pages/${page.id}`, data);
        if (!response.success) throw new Error(response.error?.message);
        return response.data;
      } else {
        const response = await api.post('/pages', data);
        if (!response.success) throw new Error(response.error?.message);
        return response.data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pages'] });
      onClose();
    },
  });

  const handleSave = (status: 'draft' | 'published' = 'draft') => {
    // Strip block IDs before saving (they are client-only)
    const cleanBlocks: Array<{ type: string; data: Record<string, unknown> }> = content.map(
      ({ type, data }) => ({ type, data })
    );
    saveMutation.mutate({
      title,
      slug,
      content: { blocks: cleanBlocks as Block[] },
      excerpt,
      metaTitle,
      metaDescription,
      showInNav,
      status,
    });
  };

  const addBlock = (type: string) => {
    const newBlock: Block = {
      id: generateBlockId(),
      type,
      data: getDefaultBlockData(type),
    };
    setContent([...content, newBlock]);
  };

  const updateBlock = (index: number, data: Record<string, unknown>) => {
    const updated = [...content];
    updated[index] = { ...updated[index], data };
    setContent(updated);
  };

  const removeBlock = (index: number) => {
    setContent(content.filter((_, i) => i !== index));
  };

  const duplicateBlock = (index: number) => {
    const block = content[index];
    const newBlock: Block = {
      id: generateBlockId(),
      type: block.type,
      data: JSON.parse(JSON.stringify(block.data)),
    };
    const updated = [...content];
    updated.splice(index + 1, 0, newBlock);
    setContent(updated);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setContent((items) => {
      const oldIndex = items.findIndex((i) => i.id === active.id);
      const newIndex = items.findIndex((i) => i.id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          {page ? 'Edit Page' : 'New Page'}
        </h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Block Palette Sidebar */}
        <div className="lg:col-span-1 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
              <LayoutTemplate className="h-4 w-4" />
              Add Blocks
            </h3>
            <div className="space-y-1">
              {blockPalette.map((item) => (
                <button
                  key={item.type}
                  onClick={() => addBlock(item.type)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left text-sm rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <div className="flex-shrink-0 w-8 h-8 rounded-md bg-primary-50 text-primary-600 flex items-center justify-center group-hover:bg-primary-100">
                    <item.icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-medium text-gray-700 group-hover:text-gray-900">{item.label}</p>
                    <p className="text-xs text-gray-400 truncate">{item.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Page Settings */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Page Settings</h3>
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={showInNav}
                onChange={(e) => setShowInNav(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Show in navigation</span>
            </label>
          </div>

          {/* Save buttons */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3">
            <button
              onClick={() => handleSave('draft')}
              disabled={saveMutation.isPending || !title || !slug}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
            >
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              Save Draft
            </button>
            <button
              onClick={() => handleSave('published')}
              disabled={saveMutation.isPending || !title || !slug}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
              Publish
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-3 space-y-4">
          {/* Section Tabs */}
          <div className="flex gap-2 border-b border-gray-200">
            <button
              onClick={() => setActiveSection('content')}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px',
                activeSection === 'content'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              Content
            </button>
            <button
              onClick={() => setActiveSection('seo')}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px',
                activeSection === 'seo'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              SEO
            </button>
            <button
              onClick={() => setActiveSection('preview')}
              className={cn(
                'px-4 py-2 text-sm font-medium border-b-2 -mb-px',
                activeSection === 'preview'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              )}
            >
              <span className="flex items-center gap-1.5">
                <Eye className="h-3.5 w-3.5" />
                Preview
              </span>
            </button>
          </div>

          {activeSection === 'content' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Page Title *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="Page title"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL Slug *</label>
                <div className="flex items-center">
                  <span className="text-gray-500 text-sm mr-1">/</span>
                  <input
                    type="text"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                    placeholder="page-url"
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </div>
              </div>

              {/* Content Blocks with DnD */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Content Blocks</label>
                <DndContext
                  id={dndId}
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={content.map((b) => b.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-3">
                      {content.map((block, index) => (
                        <SortableBlockItem
                          key={block.id}
                          block={block}
                          index={index}
                          onUpdate={(data) => updateBlock(index, data)}
                          onRemove={() => removeBlock(index)}
                          onDuplicate={() => duplicateBlock(index)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>

                {content.length === 0 && (
                  <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                    <LayoutTemplate className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>Add content blocks from the palette on the left</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeSection === 'seo' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meta Title</label>
                <input
                  type="text"
                  value={metaTitle}
                  onChange={(e) => setMetaTitle(e.target.value)}
                  placeholder="Page title for search engines"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {metaTitle.length}/60 characters (recommended max)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                <textarea
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  placeholder="Brief description for search engines"
                  rows={3}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  {metaDescription.length}/160 characters (recommended max)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Excerpt</label>
                <textarea
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  placeholder="Short summary of the page"
                  rows={2}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </div>
          )}

          {activeSection === 'preview' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center gap-2 text-xs text-gray-500">
                <Globe className="h-3.5 w-3.5" />
                Preview of /{slug || 'page-url'}
              </div>
              <div className="p-0">
                <BlockPreview blocks={content} pageTitle={title} />
              </div>
            </div>
          )}

          {saveMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
              <AlertCircle className="h-4 w-4" />
              {saveMutation.error instanceof Error ? saveMutation.error.message : 'Failed to save'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// SortableBlockItem: wraps each block with dnd-kit
// ============================================

function SortableBlockItem({
  block,
  index,
  onUpdate,
  onRemove,
  onDuplicate,
}: {
  block: Block;
  index: number;
  onUpdate: (data: Record<string, unknown>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  };

  const paletteItem = blockPalette.find((p) => p.type === block.type);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'border border-gray-200 rounded-lg p-4 bg-white',
        isDragging && 'shadow-lg ring-2 ring-primary-300'
      )}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <button
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600 rounded touch-none"
            aria-label={`Drag to reorder ${block.type} block`}
          >
            <GripVertical className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-1.5">
            {paletteItem && <paletteItem.icon className="h-3.5 w-3.5 text-gray-400" />}
            <span className="text-sm font-medium text-gray-700 capitalize">{block.type}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onDuplicate}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="Duplicate block"
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            onClick={onRemove}
            className="p-1 text-red-400 hover:text-red-600"
            title="Remove block"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
      <BlockEditor
        type={block.type}
        data={block.data}
        onChange={onUpdate}
      />
    </div>
  );
}

// ============================================
// BlockEditor
// ============================================

function BlockEditor({
  type,
  data,
  onChange,
}: {
  type: string;
  data: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
}) {
  switch (type) {
    case 'hero':
      return (
        <div className="space-y-3">
          <input
            type="text"
            value={(data.title as string) || ''}
            onChange={(e) => onChange({ ...data, title: e.target.value })}
            placeholder="Hero title"
            className="w-full rounded border border-gray-300 px-3 py-2 text-lg font-bold"
          />
          <input
            type="text"
            value={(data.subtitle as string) || ''}
            onChange={(e) => onChange({ ...data, subtitle: e.target.value })}
            placeholder="Subtitle (optional)"
            className="w-full rounded border border-gray-300 px-3 py-2"
          />
        </div>
      );

    case 'paragraph':
      return (
        <textarea
          value={(data.text as string) || ''}
          onChange={(e) => onChange({ ...data, text: e.target.value })}
          placeholder="Enter text..."
          rows={4}
          className="w-full rounded border border-gray-300 px-3 py-2"
        />
      );

    case 'heading':
      return (
        <div className="space-y-2">
          <select
            value={(data.level as string) || 'h2'}
            onChange={(e) => onChange({ ...data, level: e.target.value })}
            className="rounded border border-gray-300 px-2 py-1 text-sm"
          >
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
            <option value="h4">Heading 4</option>
          </select>
          <input
            type="text"
            value={(data.text as string) || ''}
            onChange={(e) => onChange({ ...data, text: e.target.value })}
            placeholder="Heading text"
            className="w-full rounded border border-gray-300 px-3 py-2 font-semibold"
          />
        </div>
      );

    case 'image':
      return (
        <ImageBlockEditor
          url={(data.url as string) || ''}
          alt={(data.alt as string) || ''}
          caption={(data.caption as string) || ''}
          onChange={(updates) => onChange({ ...data, ...updates })}
        />
      );

    case 'cta':
      return (
        <div className="space-y-2">
          <input
            type="text"
            value={(data.text as string) || ''}
            onChange={(e) => onChange({ ...data, text: e.target.value })}
            placeholder="Button text"
            className="w-full rounded border border-gray-300 px-3 py-2"
          />
          <input
            type="text"
            value={(data.url as string) || ''}
            onChange={(e) => onChange({ ...data, url: e.target.value })}
            placeholder="Link URL (e.g. https://... or #section)"
            className="w-full rounded border border-gray-300 px-3 py-2"
          />
          <select
            value={(data.style as string) || 'primary'}
            onChange={(e) => onChange({ ...data, style: e.target.value })}
            className="rounded border border-gray-300 px-2 py-1 text-sm"
          >
            <option value="primary">Primary (filled)</option>
            <option value="secondary">Secondary (outline)</option>
          </select>
        </div>
      );

    case 'video':
      return (
        <div className="space-y-2">
          <input
            type="text"
            value={(data.url as string) || ''}
            onChange={(e) => onChange({ ...data, url: e.target.value })}
            placeholder="YouTube or Vimeo URL"
            className="w-full rounded border border-gray-300 px-3 py-2"
          />
          <input
            type="text"
            value={(data.caption as string) || ''}
            onChange={(e) => onChange({ ...data, caption: e.target.value })}
            placeholder="Caption (optional)"
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
          />
          {(data.url as string) && (
            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-500 flex items-center gap-2">
              <Video className="h-4 w-4" />
              Video will embed from: {(data.url as string)}
            </div>
          )}
        </div>
      );

    case 'divider':
      return (
        <div className="space-y-2">
          <select
            value={(data.style as string) || 'solid'}
            onChange={(e) => onChange({ ...data, style: e.target.value })}
            className="rounded border border-gray-300 px-2 py-1 text-sm"
          >
            <option value="solid">Solid line</option>
            <option value="dashed">Dashed line</option>
            <option value="dotted">Dotted line</option>
          </select>
          <hr
            className="border-gray-300"
            style={{ borderStyle: (data.style as string) || 'solid' }}
          />
        </div>
      );

    case 'spacer':
      return (
        <div className="space-y-2">
          <label className="text-xs text-gray-500">Height: {(data.height as number) || 48}px</label>
          <input
            type="range"
            min={16}
            max={200}
            step={8}
            value={(data.height as number) || 48}
            onChange={(e) => onChange({ ...data, height: Number(e.target.value) })}
            className="w-full"
          />
          <div
            className="bg-gray-100 border border-dashed border-gray-300 rounded flex items-center justify-center text-xs text-gray-400"
            style={{ height: (data.height as number) || 48 }}
          >
            {(data.height as number) || 48}px
          </div>
        </div>
      );

    case 'columns':
      return (
        <ColumnsBlockEditor data={data} onChange={onChange} />
      );

    default:
      return (
        <textarea
          value={JSON.stringify(data, null, 2)}
          onChange={(e) => {
            try {
              onChange(JSON.parse(e.target.value));
            } catch {
              // Invalid JSON, ignore
            }
          }}
          rows={3}
          className="w-full rounded border border-gray-300 px-3 py-2 font-mono text-sm"
        />
      );
  }
}

// ============================================
// ColumnsBlockEditor
// ============================================

interface ColumnData {
  blocks: { type: string; data: Record<string, unknown> }[];
}

function ColumnsBlockEditor({
  data,
  onChange,
}: {
  data: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
}) {
  const count = (data.count as number) || 2;
  const columns = (data.columns as ColumnData[]) || [];

  const handleCountChange = (newCount: number) => {
    const newColumns = [...columns];
    while (newColumns.length < newCount) {
      newColumns.push({ blocks: [] });
    }
    onChange({ ...data, count: newCount, columns: newColumns.slice(0, newCount) });
  };

  const handleColumnTextChange = (colIndex: number, text: string) => {
    const newColumns = [...columns];
    if (!newColumns[colIndex]) {
      newColumns[colIndex] = { blocks: [] };
    }
    // Simple: each column has a single paragraph block
    newColumns[colIndex] = {
      blocks: [{ type: 'paragraph', data: { text } }],
    };
    onChange({ ...data, columns: newColumns });
  };

  const getColumnText = (colIndex: number): string => {
    const col = columns[colIndex];
    if (!col || !col.blocks || col.blocks.length === 0) return '';
    const firstBlock = col.blocks[0];
    return (firstBlock.data.text as string) || '';
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <label className="text-xs text-gray-500">Columns:</label>
        <select
          value={count}
          onChange={(e) => handleCountChange(Number(e.target.value))}
          className="rounded border border-gray-300 px-2 py-1 text-sm"
        >
          <option value={2}>2 columns</option>
          <option value={3}>3 columns</option>
        </select>
      </div>
      <div className={cn('grid gap-3', count === 2 ? 'grid-cols-2' : 'grid-cols-3')}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-2">
            <label className="block text-xs text-gray-400 mb-1">Column {i + 1}</label>
            <textarea
              value={getColumnText(i)}
              onChange={(e) => handleColumnTextChange(i, e.target.value)}
              placeholder={`Column ${i + 1} content...`}
              rows={3}
              className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================
// ImageBlockEditor (preserved from original)
// ============================================

function ImageBlockEditor({
  url,
  alt,
  caption,
  onChange,
}: {
  url: string;
  alt: string;
  caption: string;
  onChange: (data: { url?: string; alt?: string; caption?: string }) => void;
}) {
  const [showPicker, setShowPicker] = useState(false);

  const { data: mediaItems, isLoading } = useQuery({
    queryKey: ['media-images'],
    queryFn: async () => {
      const response = await api.get<{ data: MediaItem[] }>('/media?mimeType=image&limit=100');
      if (!response.success) throw new Error(response.error?.message);
      return response.data?.data || [];
    },
    enabled: showPicker,
  });

  const handleSelect = (item: MediaItem) => {
    onChange({
      url: item.url,
      alt: item.altText || item.originalName,
    });
    setShowPicker(false);
  };

  return (
    <div className="space-y-3">
      {/* Image Preview or Placeholder */}
      <div className="relative aspect-video bg-gray-100 rounded-lg overflow-hidden border border-gray-200">
        {url ? (
          <img src={url} alt={alt} className="w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
            <ImageIcon className="h-10 w-10 mb-2" />
            <span className="text-sm">No image selected</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setShowPicker(true)}
          className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100"
        >
          <ImageIcon className="h-4 w-4" />
          {url ? 'Change Image' : 'Select from Media Library'}
        </button>
        {url && (
          <button
            type="button"
            onClick={() => onChange({ url: '', alt: '' })}
            className="px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
          >
            Remove
          </button>
        )}
      </div>

      {/* URL Input (for external images) */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Or enter image URL</label>
        <input
          type="text"
          value={url}
          onChange={(e) => onChange({ url: e.target.value })}
          placeholder="https://example.com/image.jpg"
          className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
        />
      </div>

      {/* Alt Text */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Alt text (for accessibility)</label>
        <input
          type="text"
          value={alt}
          onChange={(e) => onChange({ alt: e.target.value })}
          placeholder="Describe the image"
          className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
        />
      </div>

      {/* Caption */}
      <div>
        <label className="block text-xs text-gray-500 mb-1">Caption (optional)</label>
        <input
          type="text"
          value={caption}
          onChange={(e) => onChange({ caption: e.target.value })}
          placeholder="Image caption"
          className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
        />
      </div>

      {/* Media Picker Modal */}
      {showPicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-900">Select Image</h3>
              <button onClick={() => setShowPicker(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
                </div>
              ) : mediaItems?.length === 0 ? (
                <div className="text-center py-12">
                  <ImageIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No images uploaded yet.</p>
                  <p className="text-sm text-gray-400 mt-1">Go to Media Library to upload images.</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-4">
                  {mediaItems?.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => handleSelect(item)}
                      className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-transparent hover:border-primary-500 focus:border-primary-500 focus:outline-none transition-colors"
                    >
                      <img
                        src={item.url}
                        alt={item.altText || item.originalName}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowPicker(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// BlockPreview: renders blocks as they will appear on the public site
// ============================================

function BlockPreview({ blocks, pageTitle }: { blocks: Block[]; pageTitle: string }) {
  if (blocks.length === 0 && !pageTitle) {
    return (
      <div className="p-12 text-center text-gray-400">
        <Eye className="h-8 w-8 mx-auto mb-2" />
        <p>Add content blocks to see a preview</p>
      </div>
    );
  }

  return (
    <div className="max-w-none">
      {blocks.map((block, index) => (
        <PreviewBlock key={block.id || index} block={block} />
      ))}
      {blocks.length === 0 && pageTitle && (
        <div className="p-12 text-center text-gray-400">
          <p>No blocks added yet. Add content blocks to build this page.</p>
        </div>
      )}
    </div>
  );
}

function PreviewBlock({ block }: { block: Block }) {
  const { type, data } = block;

  switch (type) {
    case 'hero':
      return (
        <div className="bg-gradient-to-br from-primary-600 to-primary-800 text-white px-8 py-16 text-center">
          <h1 className="text-3xl font-bold">{(data.title as string) || 'Hero Title'}</h1>
          {(data.subtitle as string) && (
            <p className="mt-3 text-lg text-primary-100">{data.subtitle as string}</p>
          )}
        </div>
      );

    case 'heading': {
      const level = (data.level as string) || 'h2';
      const text = (data.text as string) || 'Heading';
      const className = cn(
        'px-8 py-4 font-bold text-gray-900',
        level === 'h2' && 'text-2xl',
        level === 'h3' && 'text-xl',
        level === 'h4' && 'text-lg'
      );
      return <div className={className}>{text}</div>;
    }

    case 'paragraph':
      return (
        <div className="px-8 py-3">
          <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
            {(data.text as string) || 'Paragraph text...'}
          </p>
        </div>
      );

    case 'image':
      return (
        <div className="px-8 py-4">
          {(data.url as string) ? (
            <figure>
              <img
                src={data.url as string}
                alt={(data.alt as string) || ''}
                className="w-full rounded-lg object-cover max-h-80"
              />
              {(data.caption as string) && (
                <figcaption className="mt-2 text-center text-sm text-gray-500">
                  {data.caption as string}
                </figcaption>
              )}
            </figure>
          ) : (
            <div className="bg-gray-100 rounded-lg h-48 flex items-center justify-center text-gray-400">
              <ImageIcon className="h-8 w-8" />
            </div>
          )}
        </div>
      );

    case 'cta':
      return (
        <div className="px-8 py-6 text-center">
          <span
            className={cn(
              'inline-block px-6 py-3 rounded-lg font-medium text-sm',
              (data.style as string) === 'secondary'
                ? 'border-2 border-primary-600 text-primary-600'
                : 'bg-primary-600 text-white'
            )}
          >
            {(data.text as string) || 'Button Text'}
          </span>
        </div>
      );

    case 'video': {
      const videoUrl = (data.url as string) || '';
      const embedUrl = getVideoEmbedUrl(videoUrl);
      return (
        <div className="px-8 py-4">
          {embedUrl ? (
            <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-900">
              <iframe
                src={embedUrl}
                className="absolute inset-0 w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Video embed"
              />
            </div>
          ) : (
            <div className="bg-gray-100 rounded-lg h-48 flex items-center justify-center text-gray-400">
              <Video className="h-8 w-8 mr-2" />
              <span className="text-sm">Enter a YouTube or Vimeo URL</span>
            </div>
          )}
          {(data.caption as string) && (
            <p className="mt-2 text-center text-sm text-gray-500">{data.caption as string}</p>
          )}
        </div>
      );
    }

    case 'divider':
      return (
        <div className="px-8 py-4">
          <hr
            className="border-gray-300"
            style={{ borderStyle: (data.style as string) || 'solid' }}
          />
        </div>
      );

    case 'spacer':
      return <div style={{ height: (data.height as number) || 48 }} />;

    case 'columns': {
      const count = (data.count as number) || 2;
      const columns = (data.columns as ColumnData[]) || [];
      return (
        <div className={cn('px-8 py-4 grid gap-6', count === 2 ? 'grid-cols-2' : 'grid-cols-3')}>
          {Array.from({ length: count }).map((_, i) => {
            const col = columns[i];
            const text = col?.blocks?.[0]?.data?.text as string;
            return (
              <div key={i} className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                {text || <span className="text-gray-300">Column {i + 1}</span>}
              </div>
            );
          })}
        </div>
      );
    }

    default:
      return (
        <div className="px-8 py-4 bg-gray-50 rounded">
          <pre className="text-xs text-gray-500">{JSON.stringify(data, null, 2)}</pre>
        </div>
      );
  }
}

// ============================================
// Video URL helper
// ============================================

function getVideoEmbedUrl(url: string): string | null {
  if (!url) return null;

  // YouTube
  const ytMatch = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  );
  if (ytMatch) {
    return `https://www.youtube.com/embed/${ytMatch[1]}`;
  }

  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  return null;
}

// ============================================
// NavigationTab
// ============================================

function NavigationTab() {
  const { data: pages, isLoading } = useQuery({
    queryKey: ['pages'],
    queryFn: async () => {
      const response = await api.get<Page[]>('/pages');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const navPages = pages?.filter((p) => p.showInNav && p.status === 'published') || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Pages marked "Show in navigation" will appear in your site's menu. Edit pages to add or remove them from navigation.
      </p>

      {navPages.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Menu className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No navigation items</h3>
          <p className="text-gray-500 mt-1">Edit a page and enable "Show in navigation" to add it to your menu.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-900">Navigation Items</h3>
          </div>
          <ul className="divide-y divide-gray-200">
            {navPages
              .sort((a, b) => a.navOrder - b.navOrder)
              .map((page) => (
                <li key={page.id} className="flex items-center gap-3 px-6 py-3 hover:bg-gray-50">
                  <GripVertical className="h-4 w-4 text-gray-400" />
                  <span className="font-medium text-gray-900">{page.title}</span>
                  <span className="text-sm text-gray-500">/{page.slug}</span>
                </li>
              ))}
          </ul>
        </div>
      )}
    </div>
  );
}
